import apiClient from 'api/services/apiClient';
import { MESSAGE_COMMON } from 'constants/string/messages';
import { isApiResponseError, validateMessageResponse } from 'utils/helper';

/**
 * @name report
 * @description Get the list of reports
 * @param {string} query
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const report = async (query) => {
  const response = await apiClient.request('GET', `/admin/report${query}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name reportDeleteById
 * @description Delete a report by id
 * @param {string | number} id
 * @response success - 204 No Content
 * @response failure - 404 Not Found
 * @returns {Array.<string | number>} ids
 */
export const reportDeleteById = async (id) => {
  const response = await apiClient.request('DELETE', `/admin/report/${id}`);
  if (!isApiResponseError(response)) {
    return true;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name reportDeleteByIds
 * @description Delete multiple reports by ids
 * @param {Array.<string | number>} ids
 * @response success - 204 No Content
 * @response failure - 404 Not Found
 * @returns {Array.<string | number>} ids
 */
export const reportDeleteByIds = async (ids) => {
  const response = await apiClient.request('DELETE', '/admin/report', ids);
  if (!isApiResponseError(response)) {
    return true;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name reportExport
 * @description Export a report by id
 * @param {object} data
 * @param {string} data.id
 * @param {string} data.export_type
 * @param {string} data.lang
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {Blob} response.data
 */
export const reportExport = async (data, export_type, lang) => {
  apiClient.setCustomHeader('lang', lang ?? 'ja');

  const response = await apiClient.request('POST', `/admin/report/export?export_type=${export_type}`, data, {
    responseType: 'blob',
  });
  if (!isApiResponseError(response)) {
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'survey_export.zip';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch !== null && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    const blob = response?.data;
    const url = window.URL.createObjectURL(new Blob([blob], { type: blob.type }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    apiClient.resetHeader();
  } else {
    apiClient.resetHeader();
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name reportDetail
 * @description Get the detail of a report by id
 * @param {string | number} id
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const reportDetail = async (id) => {
  const response = await apiClient.request('GET', `/admin/report/${id}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name reportGetByDate
 * @description Get the list of reports by date
 * @param {string} child_id
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const reportGetByDate = async (child_id, queryString = '') => {
  const response = await apiClient.request('GET', `/admin/report/get-by-date/${child_id}${queryString}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};
